import React, { memo, use, useCallback, useMemo } from "react";
import { Button, Dropdown, type Key, Label, Separator, Spinner } from "@heroui/react";

import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { AppRoutes } from "@domain/routing/routeConstants";
import { UserContext } from "@domain/context/UserProvider";
import { auth } from "@domain/api/firebase/firebase";
import { logger } from "@domain/infrastructure/logger";

import { ArrowRightFromSquare, ArrowRightToSquare, ChevronDown, Person } from "@gravity-ui/icons";

import { ThemeToggle } from "@app/components/theme/ThemeToggle";

// If logging in, show nothing
// If logged in, show profile (inkl. logout) button
// If logged out, show login button
const LoginLogoutImpl = () => {
    const user = use(UserContext);
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();

    const handleAction = useCallback(
        (id: Key) => {
            if (id === "__logout") {
                auth.signOut().catch((error) => {
                    logger.error("error catched on logout", { error });
                });
                return;
            }
            navigate(id.toString());
        },
        [navigate],
    );

    const login = useCallback(() => {
        navigate(AppRoutes.authLogin);
    }, [navigate]);

    const selectedKeys = useMemo(() => [location.pathname], [location.pathname]);

    return (
        <>
            {user.fireUser && (
                <Dropdown>
                    <Dropdown.Trigger>
                        <Button
                            variant="outline"
                            isPending={user.isLoading}
                            isDisabled={user.isLoading}
                            data-testid="header-dropdown-profile"
                            className="bg-app-surface"
                        >
                            {({ isPending }) => (
                                <>
                                    {isPending ? <Spinner color="current" size="sm" /> : null}
                                    {user.fireUser.email}
                                    <ChevronDown className="size-4" />
                                </>
                            )}
                        </Button>
                    </Dropdown.Trigger>
                    <Dropdown.Popover>
                        <Dropdown.Menu aria-label="Profile menu" selectedKeys={selectedKeys} onAction={handleAction}>
                            <Dropdown.Section>
                                <Dropdown.Item
                                    id={AppRoutes.profile}
                                    textValue={intl.formatMessage({ id: "app.titles.profile" })}
                                >
                                    <Person className="size-4" />
                                    <Label>
                                        <FormattedMessage id="app.titles.profile" />
                                    </Label>
                                </Dropdown.Item>
                            </Dropdown.Section>
                        </Dropdown.Menu>
                        <Separator />
                        <div className="px-1 py-1">
                            <ThemeToggle compact />
                        </div>
                        <Separator />
                        <Dropdown.Menu aria-label="Session menu" onAction={handleAction}>
                            <Dropdown.Section>
                                <Dropdown.Item
                                    id="__logout"
                                    variant="danger"
                                    textValue={intl.formatMessage({ id: "app.header.logout" })}
                                    data-testid="header-profile-menu-item-logout"
                                >
                                    <ArrowRightFromSquare className="size-4" />
                                    <Label>
                                        <FormattedMessage id="app.header.logout" />
                                    </Label>
                                </Dropdown.Item>
                            </Dropdown.Section>
                        </Dropdown.Menu>
                    </Dropdown.Popover>
                </Dropdown>
            )}
            {!user.fireUser && (
                <Button variant="outline" onPress={login} isDisabled={user.isLoading} data-testid="header-btn-login">
                    <ArrowRightToSquare className="size-4" />
                    <FormattedMessage id="app.header.login" />
                </Button>
            )}
        </>
    );
};

export const LoginLogout = memo(LoginLogoutImpl);
